/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-nested-ternary */
/* eslint-disable import/no-cycle */
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import ResetPasswordModal from '@brands/Components/Modals/ResetPasswordModal/ResetPasswordModal';
import MedicalHistoryForm from '@brands/Dashboard/MedicalHistory/MedicalHistory/MedicalHistory';
import EditPharmacy from '@brands/Dashboard/MyProfile/Patient/Sections/Pharmacy/EditPharmacy';
import ReadOnlyPharmacy from '@brands/Dashboard/MyProfile/Patient/Sections/Pharmacy/ReadOnlyPharmacy';
import { MyProfileSubmit } from '@brands/Dashboard/MyProfile/Patient/utils/types';
import ModernReviewProfileHeader from '@brands/Dashboard/MyProfile/Components/ModernReviewProfileHeader';
import ProfileCasesSection from '@brands/Dashboard/MyProfile/Components/ProfileCasesSection';
import { config } from '@brands/config/config';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import DeleteAccount from '@brands/Dashboard/Sidenav/SidenavModals/DeleteAccount';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { RegisterMediaPayload } from '@brands/services/communication/registersConversationMedia';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalHistory } from '@brands/services/clinical_data/types/types';
import { getCurrentProfile } from '@brands/services/identity/getCurrentProfile';
import { getPatientInsurance } from '@brands/services/identity/getPatientInsurance';
import { getUserById } from '@brands/services/identity/getUserById';
import { listAllClientTags } from '@brands/services/identity/listAllClientTags';
import { scopeClientTagOptionsToActor } from '@brands/services/identity/clientTagActorScope';
import { getUserClientTagRefs, isClientTagOption } from '@brands/Utils/userClientTags';
import { listAllLanguages } from '@brands/services/identity/listAllLanguages';
import { listAllOrganizations } from '@brands/services/identity/listAllOrganizations';
import {
  ClientTag,
  Insurance,
  Language,
  Organization,
  UserProfile,
  UserRoleName,
} from '@brands/services/identity/types/UserProfile';
import { mapProfileToMyProfileSubmit } from '@brands/store/actions/patientAction';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectPageState } from '@brands/store/selectors/pageState';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { setDependentId as setPageDependentId, setIsFromSelectPatient } from '@brands/store/slices/pageStateSlice';
import { setPatientMyProfile } from '@brands/store/slices/patientMyProfileSlice';
import { setSelectedPatient } from '@brands/store/slices/patientFormSlice';
import { setSelectedPatientByAdmin } from '@brands/store/slices/selectedPatientSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { Option, OptionLanguage } from '@brands/Utils/selectOptions';

import { parsePhoneNumber } from 'libphonenumber-js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoInformationCircle } from 'react-icons/io5';
import { RiDeleteBin6Line, RiRestartLine } from 'react-icons/ri';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import collectorStyles from '../../../../Dashboard/Collector/EditUser/collectorEditUser.module.scss';
import CollectorMedicalHistoryTab from '../../../../Dashboard/Collector/EditUser/CollectorMedicalHistoryTab';
import CollectorInsuranceTab from '../../../../Dashboard/Collector/EditUser/CollectorInsuranceTab';
import CollectorPharmacyTab from '../../../../Dashboard/Collector/EditUser/CollectorPharmacyTab';
import CollectorAccountSettingsTab from '../../../../Dashboard/Collector/EditUser/CollectorAccountSettingsTab';
import ChangeUserStatusModal from '../../../Modal/ChangeUserStatusModal';
import AdminResetPasswordModal from '../../../Modal/ResetPasswordModal';
import { deleteUserById } from '../../../../../services/identity/deleteUserById';

import { getUserAvatar } from '../../../../../services/identity/getUserAvatar';
import { MultiValue } from 'react-select';

import BlueCircle from '../../../../../assets/icons/light-blue-circle.svg';
import GrayCircle from '../../../../../assets/icons/light-gray-circle.svg';
import EditAccountSecurity from '../AccountSecurity/EditAccountSecurity';
import ReadOnlyAccountSecurity from '../AccountSecurity/ReadOnlyAccountSecurity';
import EditInsurance from '../Insurance/EditInsurance';
import ReadOnlyInsurance from '../Insurance/ReadOnlyInsurance';
import ReadOnlyOrganizations from '../Organizations/ReadOnlyOrganizations';
import ProfileCommonHeader from '../ProfileCommonHeader/ProfileCommonHeader';
import EditDependent from './EditDependent';
import EditProfile from './EditProfile';
import ReadOnlyDependent from './ReadOnlyDependent';
import ReadOnlyProfile from './ReadOnlyProfile';
import styles from './styles.module.scss';

type ReviewMyProfileProps = {
  reviewProfile?: boolean;
};

export const isValidPhoneNumberFormat = (phoneNumber: string): boolean => {
  const phoneNumberPattern = /^\+?1?[-.\s]?\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})$/;

  return phoneNumberPattern.test(phoneNumber);
};

export const displayPhoneNumber = (pharmacyPhoneNumber: string): string => {
  const phoneNumber = parsePhoneNumber(pharmacyPhoneNumber, 'US');
  if (phoneNumber) {
    return phoneNumber.formatNational();
  }
  return '';
};


const MyProfile = ({ reviewProfile = false }: ReviewMyProfileProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const hasFetchedRef = useRef(false);
  const hasFetchedUserRef = useRef<string | null>(null);
  const { selectedPatient: currentPatient } = useAppSelector(selectPatient);
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory | null>(null);
  const params = useParams();
  const userId = params.userId as string;
  const roleName = ((params.targetRole ?? params.role) as string).toLowerCase();
  const location = useLocation();
  const { userInfo } = useAppSelector(selectAuth);
  const [currentTab, setCurrentTab] = useState('profile');
  /**
   * One ref per section rather than a single shared ref: in the non-tabbed inline view
   * (`renderProfileTab`, used when `!showTabs`) Profile and Insurance can be open and
   * dirty at the same time, so a shared ref would only ever point at whichever form
   * mounted last, submitting the wrong one.
   */
  const profileFormRef = useRef<HTMLFormElement>(null);
  const medicalFormRef = useRef<HTMLFormElement>(null);
  const insuranceFormRef = useRef<HTMLFormElement>(null);
  const [isProfileTabDirty, setIsProfileTabDirty] = useState(false);
  const [isMedicalDirty, setIsMedicalDirty] = useState(false);
  const [isInsuranceTabDirty, setIsInsuranceTabDirty] = useState(false);
  const [thisUser, setThisUser] = useState<UserProfile>();
  const [loading, setLoading] = useState(true);
  const [myProfileForm, setMyProfileForm] = useState<any>({} as any);
  const [isEditProfile, setIsEditProfile] = useState(false);
  const [isEditInsurance, setIsEditInsurance] = useState(false);
  const [isEditDependent, setIsEditDependent] = useState(false);
  const [isEditSecurity, setIsEditSecurity] = useState(false);
  const [isEditPharmacy, setIsEditPharmacy] = useState(false);
  const [closeAccountModal, setCloseAccountModal] = useState(false);
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [avatarAction, setAvatarAction] = useState<string>('');
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [organizationOptions, setOrganizationOptions] = useState<Option[]>([]);
  const [clientTagOptions, setClientTagOptions] = useState<Option[]>([]);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);
  const [currentAvatarURL, setCurrentAvatarURL] = useState<string>('');
  const [validationResponseId, setValidationResponseId] = useState('');
  const [image, setImage] = React.useState<string>();
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [patientInsurance, setPatientInsurance] = useState<Insurance[]>([]);
  const [pharmacyName, setPharmacyName] = useState('');
  const [pharmacyAddress, setPharmacyAddress] = useState('');
  const [pharmacyPhoneNumber, setPharmacyPhoneNumber] = useState('');
  const [dependentId, setDependentId] = useState<number | undefined>(undefined);
  const [selection, setSelection] = React.useState<[number | null, number | null] | null>(null);
  const { isFromSelectPatient, isEditInsuranceFlag, selectedDependentId } = useAppSelector(selectPageState);
  const [showDeleteDependentConfirmationModal, setShowDeleteDependentConfirmationModal] = useState(false);
  const [adminDeactivateModal, setAdminDeactivateModal] = useState('');
  const [adminResetEmailSent, setAdminResetEmailSent] = useState(false);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [adminResetPasswordConfirmation, setAdminResetPasswordConfirmation] = useState<{
    isOpen: boolean;
    userState: string;
  }>({
    isOpen: false,
    userState: '',
  });
  const navigateRaw = useNavigate();

  const isPatientProfile = useMemo(
    () => roleName === UserRoleName.Patient.toLowerCase() || roleName === UserRoleName.Dependent.toLowerCase(),
    [roleName],
  );
  // The router lowercases role-prefixed URLs (`/SuperAdmin/...` becomes
  // `/superadmin/...`), so compare the role segment case-insensitively.
  const isAdminReviewProfilePath = useMemo(
    () => location.pathname.toLowerCase().startsWith(`/${userInfo.role.name.toLowerCase()}/review-profile/`),
    [location.pathname, userInfo.role.name],
  );
  const isAdminCaseCreationReviewPath = useMemo(
    () => location.pathname.startsWith('/admin/review-profile/'),
    [location.pathname],
  );
  // Admins land on this page either from case creation, when the patient's
  // profile still needs completing (CreateConsultationFor navigates here with
  // the chosen case type in the navigation state), or by opening the patient's
  // profile directly. Both use the same `/{role}/review-profile/...` URL, so the
  // state is what tells the case-creation detour apart from a direct visit.
  const isAdminCaseCreationReview = useMemo(
    () =>
      isAdminCaseCreationReviewPath ||
      (isAdminReviewProfilePath && Boolean((location.state as { caseType?: string } | null)?.caseType)),
    [isAdminCaseCreationReviewPath, isAdminReviewProfilePath, location.state],
  );
  const adminReviewBasePath = useMemo(
    () => (isAdminCaseCreationReviewPath ? '/admin/review-profile' : `/${userInfo.role.name}/review-profile`),
    [isAdminCaseCreationReviewPath, userInfo.role.name],
  );

  const isStaffCaseCreationReview = useMemo(
    () =>
      Boolean(reviewProfile) &&
      ((userInfo.role.name === UserRoleName.Collector &&
        location.pathname.startsWith('/collector/case-creation/')) ||
        (userInfo.role.name === UserRoleName.CareAdvisor &&
          location.pathname.startsWith('/careadvisor/case-creation/')) ||
        ((userInfo.role.name === UserRoleName.Admin ||
          userInfo.role.name === UserRoleName.SuperAdmin ||
          userInfo.role.name === UserRoleName.OrganizationAdmin) &&
          isFromSelectPatient &&
          isAdminCaseCreationReview &&
          isPatientProfile)),
    [reviewProfile, userInfo.role.name, location.pathname, isFromSelectPatient, isAdminCaseCreationReview, isPatientProfile],
  );
  const isAdminViewing = useMemo(
    () =>
      reviewProfile &&
      (userInfo.role.name === UserRoleName.Admin ||
        userInfo.role.name === UserRoleName.SuperAdmin ||
        userInfo.role.name === UserRoleName.OrganizationAdmin ||
        userInfo.role.name === UserRoleName.Collector ||
        userInfo.role.name === UserRoleName.CareAdvisor),
    [reviewProfile, userInfo.role.name],
  );

  const isAdminReviewProfile = useMemo(
    () =>
      Boolean(reviewProfile) &&
      (userInfo.role.name === UserRoleName.Admin ||
        userInfo.role.name === UserRoleName.SuperAdmin ||
        userInfo.role.name === UserRoleName.OrganizationAdmin),
    [reviewProfile, userInfo.role.name],
  );
  const shouldShowAdminSaveSnackbar = isAdminReviewProfile && !isStaffCaseCreationReview;

  useEffect(() => {
    if (!isAdminReviewProfile || !isPatientProfile || isAdminCaseCreationReview || !isFromSelectPatient) return;

    dispatch(setIsFromSelectPatient(false));
    dispatch(setPageDependentId(null));
  }, [dispatch, isAdminReviewProfile, isPatientProfile, isAdminCaseCreationReview, isFromSelectPatient]);

  /** URL says Patient/Dependent but logged-in staff may be Collector/CA — use scoped /collector|/care_advisor APIs. */
  const identityApiRoleForScopedServices = useMemo((): UserRoleName | undefined => {
    if (userInfo.role.name === UserRoleName.Collector || userInfo.role.name === UserRoleName.CareAdvisor) {
      return userInfo.role.name;
    }
    return undefined;
  }, [userInfo.role.name]);

  const handleCreateNewCase = useCallback(() => {
    if (!thisUser) return;
    dispatch(
      setSelectedPatient({
        value: thisUser.id.toString(),
        label: thisUser.full_name,
      }),
    );
    dispatch(setSelectedPatientByAdmin({ selectedPatient: thisUser }));
    navigateRaw(
      `/${userInfo.role.name}/review-profile/${roleName || UserRoleName.Patient}/${userId}/create-consultation-for`,
    );
  }, [dispatch, navigateRaw, roleName, thisUser, userId, userInfo.role.name]);

  /**
   * Passed to ModernReviewProfileHeader's ProfileImage as `triggerProfileAvatar`. Must be
   * stable and a no-op when the value hasn't changed: ProfileImage re-fires its own
   * `triggerProfileAvatar` effect whenever this function's identity changes, so an inline
   * arrow function here (recreated every render) combined with always producing a new
   * `myProfileForm` object caused an infinite render loop. MyProfile re-renders, the effect
   * fires again, `setMyProfileForm` produces a new reference, EditProfile's hydration effect
   * sees a new `myProfileForm` and remounts the form (bumping its `key`), which is what made
   * every input appear to reset on focus.
   */
  const handleAdminAvatarTrigger = useCallback((avatarUrl: string) => {
    setMyProfileForm((prev: MyProfileSubmit) => (prev.profile_img === avatarUrl ? prev : { ...prev, profile_img: avatarUrl }));
  }, []);

  const profileTabs = useMemo(() => {
    const ALL_TABS = [
      { label: 'Profile', value: 'profile' },
      { label: 'Medical history', value: 'medical_history' },
      { label: 'Insurance', value: 'insurance' },
      { label: 'Pharmacy', value: 'pharmacy' },
      { label: 'Cases', value: 'cases' },
      { label: 'Account settings', value: 'account_settings' },
    ];

    const hiddenTabs = new Set([
      ...(userInfo.role.name === UserRoleName.Admin ? ['medical_history'] : []),
      ...(userInfo.role.name === UserRoleName.Admin ? ['dental_history'] : []),
      ...(config.features.multiStage ? [] : ['cases']),
      ...(roleName === UserRoleName.Dependent.toLowerCase() ||
      (userInfo.role.name !== UserRoleName.Admin && !isAdminViewing && isPatientProfile)
        ? ['account_settings']
        : []),
    ]);

    return ALL_TABS.filter((tab) => !hiddenTabs.has(tab.value));
  }, [isPatientProfile, isAdminViewing, userInfo.role.name, roleName]);

  /** Whether tabs are rendered at all. When false, `renderProfileTab()` shows Profile and
   * Insurance as inline sections that can both be open/dirty at once, so dirtiness can't be
   * scoped to a single "active" tab the way it can when a tab bar is shown. */
  const showTabs = isAdminViewing && profileTabs.length > 1;

  const isCurrentTabDirty = showTabs
    ? currentTab === 'profile'
      ? isProfileTabDirty
      : currentTab === 'medical_history'
      ? isMedicalDirty
      : currentTab === 'insurance'
      ? isInsuranceTabDirty
      : false
    : isProfileTabDirty || isInsuranceTabDirty;

  const { showModal, confirmNavigation, hideModal, handleBlockedNavigation } = useUnsavedChangesWarning(
    isCurrentTabDirty,
    profileFormRef,
  );

  /**
   * Each section keeps its own form ref (see the refs above), so submit whichever ones are
   * actually dirty instead of relying on the hook's single-ref `requestSubmit()`, which would
   * only ever hit one of Profile/Medical History/Insurance's forms even when more than one is
   * dirty at once (possible whenever tabs aren't shown).
   */
  const handleModalSaveChanges = useCallback((): void => {
    if (isProfileTabDirty) profileFormRef.current?.requestSubmit();
    if (isMedicalDirty) medicalFormRef.current?.requestSubmit();
    if (isInsuranceTabDirty) insuranceFormRef.current?.requestSubmit();
    hideModal();
  }, [isProfileTabDirty, isMedicalDirty, isInsuranceTabDirty, hideModal]);

  const handleTabChange = useCallback(
    (nextTab: string) => {
      if (nextTab === currentTab) return;
      handleBlockedNavigation(() => {
        closeSnackbar();
        setCurrentTab(nextTab);
        window.history.replaceState(null, '', `#${nextTab}`);
      });
    },
    [closeSnackbar, currentTab, handleBlockedNavigation],
  );

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (profileTabs.find((t) => t.value === hash)) {
      setCurrentTab(hash);
    }
  }, []);


  const getFormAnswers = useCallback(async (createdByPatientId: number | undefined) => {
    setLoading(true);
    const { medical_history } = await listClinicalData(createdByPatientId ?? 0, userInfo.role.name);

    setMedicalHistory(medical_history);

    setPharmacyName(medical_history?.pharmacy_name || '');

    setPharmacyAddress(
      [
        medical_history?.pharmacy_street_address,
        medical_history?.pharmacy_city,
        medical_history?.pharmacy_state,
        medical_history?.pharmacy_zip_code,
      ]
        .filter(Boolean)
        .join(', '),
    );

    setPharmacyPhoneNumber(
      isValidPhoneNumberFormat(medical_history?.pharmacy_phone_number || '')
        ? displayPhoneNumber(medical_history?.pharmacy_phone_number || '')
        : medical_history?.pharmacy_phone_number || '',
    );

    setLoading(false);
  }, [userInfo.role.name]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    if (!currentPatient?.id) return;

    hasFetchedRef.current = true;

    (async () => {
      setLoading(true);
      setPharmacyAddress('');
      setPharmacyPhoneNumber('');
      await getFormAnswers(isEditDependent && dependentId ? dependentId : Number(currentPatient.id));
      setLoading(false);
    })();
  }, [thisUser, dependentId, isEditDependent, currentPatient?.id]);

  const ref = React.useRef<HTMLInputElement>(null);
  const addDependent = (): void => {
    setDependentId(undefined);
    setIsEditProfile(false);
    hasFetchedRef.current = false;
    setIsEditDependent(true);
  };

  const onEditProfile = (): void => {
    closeSnackbar();
    setAvatarAction('');
    setIsEditDependent(false);
    setIsEditProfile(true);
  };

  const onEditInsurance = (): void => {
    setIsEditInsurance(true);
  };

  const onEditDependent = (id: number): void => {
    closeSnackbar();
    if (isAdminReviewProfile) {
      const targetPath = `/${userInfo.role.name.toLowerCase()}/review-profile/Dependent/${id}`;
      // Routes.tsx lowercases the role segment, so compare case-insensitively. A mismatch here pushed a
      // duplicate entry without the URL hash, which dropped the active tab (e.g. #cases) on every load.
      if (location.pathname.toLowerCase() !== targetPath.toLowerCase()) {
        navigateRaw(targetPath);
        return;
      }
    }
    setDependentId(id);
    setIsEditProfile(false);
    hasFetchedRef.current = false;
    setIsEditDependent(true);
  };

  const onEditPharmacy = (): void => {
    setIsEditPharmacy(true);
  };

  const fetchUserDetails = async (): Promise<void> => {
    setLoading(true);
    const userDetails = await getCurrentProfile();
    setThisUser(userDetails);
    setLoading(false);
  };

  const fetchUserInsurance = async (): Promise<void> => {
    if (!reviewProfile) {
      const userInsurance = await getPatientInsurance(
        Number(currentPatient?.id) !== Number(userInfo.id) ? Number(currentPatient?.id) : undefined,
      );
      setPatientInsurance(userInsurance);
    } else {
      const userInsurance = await getPatientInsurance(thisUser?.id);
      setPatientInsurance(userInsurance);
    }
  };

  const fetchLanguageOptions = async (): Promise<void> => {
    const data = await listAllLanguages();
    const transformedData = data.map((language) => ({
      value: language.locale,
      label: language.name,
      id: language.id,
    }));
    setLanguageOptions(transformedData);
  };

  const fetchOrganizationOptions = async (): Promise<void> => {
    try {
      const data = await listAllOrganizations(
        userInfo.role.name === UserRoleName.Collector || userInfo.role.name === UserRoleName.CareAdvisor
          ? userInfo.role.name
          : undefined,
      );
      if (userInfo.role.name === UserRoleName.OrganizationAdmin) {
        data.nodes = data.nodes.filter((org) => org.id === userInfo.organizations[0].id);
      }
      const transformedData = data.nodes.map((organization) => ({
        value: organization.id,
        label: organization.name,
        id: organization.id,
      }));
      setOrganizationOptions(transformedData);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  const fetchClientTagsOptions = async (): Promise<void> => {
    try {
      const data = await listAllClientTags(identityApiRoleForScopedServices);
      setAllClientTags(data.nodes);
      const transformedData = data.nodes.map((clientTag) => ({
        value: clientTag.code,
        label: clientTag.name,
        id: clientTag.id,
      }));
      setClientTagOptions(scopeClientTagOptionsToActor(transformedData, userInfo));
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    fetchLanguageOptions();
    if (
      reviewProfile ||
      userInfo.role.name === UserRoleName.Admin ||
      userInfo.role.name === UserRoleName.SuperAdmin ||
      userInfo.role.name === UserRoleName.OrganizationAdmin ||
      userInfo.role.name === UserRoleName.Collector ||
      userInfo.role.name === UserRoleName.CareAdvisor
    ) {
      fetchOrganizationOptions();
      fetchClientTagsOptions();
    }
  }, []);

  useEffect(() => {
    if (hasFetchedUserRef.current === (userId ?? null)) return;
    hasFetchedUserRef.current = userId ?? null;

    const fetchUser = async (): Promise<void> => {
      try {
        setLoading(true);
        if (reviewProfile && userId) {
          const res = await getUserById(Number(userId), identityApiRoleForScopedServices);
          dispatch(setSelectedPatientByAdmin({ selectedPatient: res }));
          setThisUser(res);
        } else {
          await fetchUserDetails();
        }
      } catch (error: unknown) {
        displayErrorDetails(error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId, reviewProfile]);

  useEffect(() => {
    fetchUserInsurance();
  }, [thisUser]);

  useEffect(() => {
    if (isAdminReviewProfile && isPatientProfile && !isAdminCaseCreationReview) return;

    if (isFromSelectPatient && selectedDependentId) {
      onEditDependent(selectedDependentId);
    } else if (isFromSelectPatient && selectedDependentId === null) {
      onEditProfile();
    }
  }, [isFromSelectPatient, selectedDependentId, isAdminReviewProfile, isPatientProfile, isAdminCaseCreationReview]);

  useEffect(() => {
    if (!isStaffCaseCreationReview && !(isAdminReviewProfile && isPatientProfile)) return;
    if (roleName === UserRoleName.Dependent.toLowerCase()) {
      const depId = Number(userId);
      if (depId) onEditDependent(depId);
    } else {
      setIsEditDependent(false);
      setIsEditProfile(true);
    }
  }, [isStaffCaseCreationReview, isAdminReviewProfile, isPatientProfile, roleName, userId]);

  useEffect(() => {
    const actualRole = thisUser?.role?.name;
    if (!reviewProfile || !userId || (actualRole !== UserRoleName.Patient && actualRole !== UserRoleName.Dependent)) {
      return;
    }
    if (thisUser?.id !== Number(userId)) return;
    if (actualRole?.toLowerCase() === roleName) return;

    if (location.pathname.startsWith(`/${userInfo.role.name}/review-profile/`)) {
      navigateRaw(`/${userInfo.role.name}/review-profile/${actualRole?.toLowerCase()}/${userId}`, { replace: true });
    } else if (location.pathname.startsWith('/careadvisor/case-creation/')) {
      navigateRaw(`/careadvisor/case-creation/${actualRole?.toLowerCase()}/${userId}`, { replace: true });
    } else if (location.pathname.startsWith('/collector/case-creation/')) {
      navigateRaw(`/collector/case-creation/${actualRole?.toLowerCase()}/${userId}`, { replace: true });
    }
  }, [
    reviewProfile,
    userId,
    roleName,
    thisUser?.role?.name,
    location.pathname,
    navigateRaw,
    userInfo.role.name,
    adminReviewBasePath,
  ]);

  const fetchProfile = (): void => {
    const mappedProfile = mapProfileToMyProfileSubmit(thisUser, patientInsurance);

    function isLanguageArray(value: Language[] | string[] | Organization[]): value is Language[] {
      return Array.isArray(value) && value.length > 0 && value[0] instanceof Object && 'id' in value[0];
    }
    let selectedLanguage: MultiValue<OptionLanguage> = [];
    if (isLanguageArray(mappedProfile.languages)) {
      for (let i = 0; i < languageOptions.length; i++) {
        for (let j = 0; j < mappedProfile.languages.length; j++) {
          if (Number(languageOptions[i].id) === Number(mappedProfile.languages[j].id)) {
            selectedLanguage = [...selectedLanguage, languageOptions[i]];
          }
        }
      }
    }

    let selectedOrganizations: MultiValue<Option> = [];
    if (
      mappedProfile.organizations &&
      (mappedProfile.organizations as Organization[]) &&
      isLanguageArray(mappedProfile.organizations)
    ) {
      for (let i = 0; i < organizationOptions.length; i++) {
        for (let j = 0; j < mappedProfile.organizations.length; j++) {
          if (organizationOptions[i].id === mappedProfile.organizations[j].id) {
            selectedOrganizations = [...selectedOrganizations, organizationOptions[i]];
          }
        }
      }
    }

    let selectedOptions: MultiValue<Option> = [];

    selectedOptions = getUserClientTagRefs(thisUser)
      .map((ref) => clientTagOptions.find((option): option is Option => isClientTagOption(ref, option)))
      .filter((option): option is Option => option !== undefined);

    const selectedDivisionOptions: MultiValue<Option> = (thisUser?.divisions || []).map(
      (division) => ({ value: String(division.id), label: division.name, id: division.id }) as Option,
    );

    dispatch(
      setPatientMyProfile({
        ...(mappedProfile as MyProfileSubmit),
        language: selectedLanguage,
        profile_img: currentAvatarURL,
        organization: selectedOrganizations,
      }),
    );
    setMyProfileForm({
      ...(mappedProfile as MyProfileSubmit),
      language: selectedLanguage,
      profile_img: currentAvatarURL,
      organization: selectedOrganizations,
      organizations: mappedProfile.organizations,
      clientTag: selectedOptions,
      clientDivision: selectedDivisionOptions,
    });
  };
  useEffect(() => {
    if (thisUser) {
      fetchProfile();
    }
  }, [thisUser, languageOptions, patientInsurance]);

  React.useLayoutEffect(() => {
    if (selection && ref.current) {
      [ref.current.selectionStart, ref.current.selectionEnd] = selection;
    }
  }, [selection]);

  useEffect(() => {
    if (thisUser) {
      fetchProfile();
      setLoading(false);
    }
  }, [userInfo, languageOptions, patientInsurance]);


  if (loading) {
    return <Loading fullScreen />;
  }

  if (!thisUser) {
    return <Loading fullScreen />;
  }

  const renderProfile = (): React.ReactNode => {
    let componentToRender: React.JSX.Element;

    if (isEditProfile || (isAdminReviewProfile && roleName !== UserRoleName.Dependent.toLowerCase() && !isEditDependent)) {
      componentToRender = (
        <EditProfile
          reviewProfile={reviewProfile}
          id={userId}
          setImage={setImage}
          image={image as string}
          myProfileForm={myProfileForm}
          setMyProfileForm={setMyProfileForm}
          setIsEditProfile={setIsEditProfile}
          setSelection={setSelection}
          setCurrentAvatarURL={setCurrentAvatarURL}
          currentAvatarURL={currentAvatarURL}
          thisUser={thisUser as UserProfile}
          setThisUser={setThisUser as React.Dispatch<React.SetStateAction<UserProfile>>}
          avatarAction={avatarAction}
          setAvatarAction={setAvatarAction}
          languageOptions={languageOptions}
          clientTagOptions={clientTagOptions}
          allClientTags={allClientTags}
          validationResponseId={validationResponseId}
          setValidationResponseId={setValidationResponseId}
          onSaveSuccess={
            shouldShowAdminSaveSnackbar ? () => showSnackbar('Your changes have been saved', 'success') : undefined
          }
          externalAvatarPayload={avatarPayload}
          externalAvatarFile={avatarFile}
          onExternalAvatarSaved={() => {
            setAvatarAction('');
            setAvatarPayload(null);
            setAvatarFile(null);
          }}
          formRef={profileFormRef}
          setIsDirty={setIsProfileTabDirty}
        />
      );
    } else if (isEditDependent || (isAdminReviewProfile && roleName === UserRoleName.Dependent.toLowerCase())) {
      componentToRender = (
        <EditDependent
          setIsEditDependent={setIsEditDependent}
          setSelection={setSelection}
          setThisUser={setThisUser as React.Dispatch<React.SetStateAction<UserProfile>>}
          dependentId={dependentId !== undefined ? dependentId : (roleName === UserRoleName.Dependent.toLowerCase() ? Number(userId) : 0)}
          languageOptions={languageOptions}
          reviewProfile={reviewProfile}
          validationResponseId={validationResponseId}
          setValidationResponseId={setValidationResponseId}
          isFromSelectNeeds={false}
          showDeleteDependentConfirmationModal={showDeleteDependentConfirmationModal}
          setShowDeleteDependentConfirmationModal={setShowDeleteDependentConfirmationModal}
          roleName={roleName}
          externalAvatarPayload={avatarPayload}
          externalAvatarFile={avatarFile}
          externalAvatarAction={avatarAction}
          onSaveSuccess={
            shouldShowAdminSaveSnackbar ? () => showSnackbar('Your changes have been saved', 'success') : undefined
          }
          onExternalAvatarSaved={() => {
            setAvatarAction('');
            setAvatarPayload(null);
            setAvatarFile(null);
          }}
          formRef={profileFormRef}
          setIsDirty={setIsProfileTabDirty}
        />
      );
    } else {
      componentToRender = (
        <ReadOnlyProfile
          id={userId}
          onEditProfile={onEditProfile}
          onEditDependent={onEditDependent}
          setImage={setImage}
          image={image!}
          thisUser={thisUser as UserProfile}
          setThisUser={setThisUser as React.Dispatch<React.SetStateAction<UserProfile>>}
          reviewProfile={reviewProfile}
          roleName={roleName}
          organizationOptions={organizationOptions}
          clientTagOptions={clientTagOptions}
          setDependentId={setDependentId}
        />
      );
    }
    return componentToRender;
  };
  const showStaffCaseCreationTabs =
    isStaffCaseCreationReview &&
    (userInfo.role.name === UserRoleName.Collector || userInfo.role.name === UserRoleName.CareAdvisor) &&
    profileTabs.length > 1;
  const isAddingNewDependent = isEditDependent && !dependentId;

  const renderProfileTab = (): React.ReactNode => (
    <>
      {renderProfile()}
      {(!isFromSelectPatient || isEditInsuranceFlag) && !isEditDependent && (
        <>
          {userInfo.role.name === UserRoleName.Patient && <div className={styles.separator} />}
          {userInfo.role.name === UserRoleName.Patient && <ReadOnlyOrganizations />}
        </>
      )}
      {(!isFromSelectPatient || isEditInsuranceFlag) && !isEditDependent && !showTabs && (
        <>
          {currentPatient?.role?.name === UserRoleName.Patient && (
            <>
              <div className={styles.separator} />
              {isEditSecurity ? (
                <EditAccountSecurity setIsEditSecurity={setIsEditSecurity} thisUser={thisUser} />
              ) : (
                <ReadOnlyAccountSecurity setIsEditSecurity={setIsEditSecurity} thisUser={thisUser} />
              )}
            </>
          )}
        </>
      )}
      {(!isFromSelectPatient || isEditInsuranceFlag) && !showTabs && (
        <>
          <div className={styles.separator} />
          {isEditInsurance || (isFromSelectPatient && isEditInsuranceFlag) ? (
            <EditInsurance
              myProfileForm={myProfileForm}
              patientInsurance={patientInsurance!}
              setPatientInsurance={setPatientInsurance}
              setIsEditInsurance={setIsEditInsurance}
              setSelection={setSelection}
              loading={loading}
              thisUser={thisUser as UserProfile}
              formRef={insuranceFormRef}
              setIsDirty={setIsInsuranceTabDirty}
            />
          ) : !isFromSelectPatient || isEditInsuranceFlag ? (
            <ReadOnlyInsurance onEditInsurance={onEditInsurance} patientInsurance={patientInsurance!} />
          ) : null}
        </>
      )}
      {(!isFromSelectPatient || isEditInsuranceFlag) &&
        (!isEditDependent || (isEditDependent && dependentId !== undefined)) &&
        !showTabs && (
          <>
            {!isEditPharmacy && <div className={styles.separator} />}
            {!isEditPharmacy ? (
              <ReadOnlyPharmacy
                onEditPharmacy={onEditPharmacy}
                pharmacyName={pharmacyName}
                pharmacyAddress={pharmacyAddress}
                pharmacyPhoneNumber={pharmacyPhoneNumber}
              />
            ) : (
              <EditPharmacy
                thisUser={thisUser as UserProfile}
                setThisUser={setThisUser}
                medicalHistoryForm={medicalHistory as MedicalHistory}
                setIsEditPharmacy={setIsEditPharmacy}
                loading={loading}
                setPharmacyName={setPharmacyName}
                setPharmacyAddress={setPharmacyAddress}
                setPharmacyPhoneNumber={setPharmacyPhoneNumber}
                dependentId={dependentId}
              />
            )}
          </>
        )}
      {(!isFromSelectPatient || isEditInsuranceFlag) && !isEditDependent && !showTabs && (
        <div className={styles.separator} />
      )}
      {(!isFromSelectPatient || isEditInsuranceFlag) &&
        !isEditDependent &&
        thisUser?.role.name !== UserRoleName.Dependent && (
          <ReadOnlyDependent
            onEditDependent={onEditDependent}
            addDependent={addDependent}
            reviewProfile={reviewProfile as boolean}
          />
        )}
      {(!isFromSelectPatient || isEditInsuranceFlag) && !reviewProfile && !isEditDependent && (
        <div className={styles.closeAccountContainer}>
          <span className={styles.closeAccount} onClick={() => setCloseAccountModal(true)}>
            <RiDeleteBin6Line />
            Delete Account
          </span>
        </div>
      )}
      {(!isFromSelectPatient || isEditInsuranceFlag) &&
      isEditDependent &&
      !reviewProfile &&
      thisUser &&
      dependentId !== undefined &&
      dependentId !== 0 &&
      dependentId.toString() !== '' ? (
        <div className={`${styles.closeAccountContainer} `}>
          <div
            className={`${styles.closeAccount} ${
              (thisUser as UserProfile)?.status === 'Active' ? '' : styles.inactiveClass
            }`}
            onClick={() =>
              (thisUser as UserProfile)?.status === 'Active' ? setShowDeleteDependentConfirmationModal(true) : ''
            }
          >
            <RiDeleteBin6Line />
            Delete Dependent
          </div>
        </div>
      ) : null}
      {!reviewProfile && !isEditDependent && !showTabs && (
        <div className={styles.closeAccountContainer} style={{ width: 'auto' }}>
          <span className={styles.closeAccount} onClick={() => setResetPasswordModal(true)}>
            <RiRestartLine />
            Reset Password
          </span>
        </div>
      )}
    </>
  );

  const renderInsuranceTab = (): React.ReactNode => (
    <>
      {isEditInsurance ? (
        <EditInsurance
          myProfileForm={myProfileForm}
          patientInsurance={patientInsurance!}
          setPatientInsurance={setPatientInsurance}
          setIsEditInsurance={setIsEditInsurance}
          setSelection={setSelection}
          loading={loading}
          thisUser={thisUser as UserProfile}
          formRef={insuranceFormRef}
          setIsDirty={setIsInsuranceTabDirty}
        />
      ) : (
        <ReadOnlyInsurance onEditInsurance={onEditInsurance} patientInsurance={patientInsurance!} />
      )}
    </>
  );

  const renderPharmacyTab = (): React.ReactNode => (
    <>
      {isEditPharmacy ? (
        <EditPharmacy
          thisUser={thisUser as UserProfile}
          setThisUser={setThisUser}
          medicalHistoryForm={medicalHistory as MedicalHistory}
          setIsEditPharmacy={setIsEditPharmacy}
          loading={loading}
          setPharmacyName={setPharmacyName}
          setPharmacyAddress={setPharmacyAddress}
          setPharmacyPhoneNumber={setPharmacyPhoneNumber}
          dependentId={dependentId}
        />
      ) : (
        <ReadOnlyPharmacy
          onEditPharmacy={onEditPharmacy}
          pharmacyName={pharmacyName}
          pharmacyAddress={pharmacyAddress}
          pharmacyPhoneNumber={pharmacyPhoneNumber}
        />
      )}
    </>
  );

  const renderAccountSettingsTab = (): React.ReactNode => (
    <>
      {currentPatient?.role?.name === UserRoleName.Patient && (
        <>
          {isEditSecurity ? (
            <EditAccountSecurity setIsEditSecurity={setIsEditSecurity} thisUser={thisUser} />
          ) : (
            <ReadOnlyAccountSecurity setIsEditSecurity={setIsEditSecurity} thisUser={thisUser} />
          )}
        </>
      )}
      {!isEditDependent && (
        <>
          <div className={styles.separator} />
          <div className={styles.closeAccountContainer} style={{ width: 'auto' }}>
            <span className={styles.closeAccount} onClick={() => setResetPasswordModal(true)}>
              <RiRestartLine />
              Reset Password
            </span>
          </div>
        </>
      )}
    </>
  );

  const renderTabContent = (): React.ReactNode => {
    switch (currentTab) {
      case 'profile':
        return renderProfileTab();
      case 'medical_history':
        return <MedicalHistoryForm isProfileCard formRef={medicalFormRef} setIsDirty={setIsMedicalDirty} />;
      case 'insurance':
        return renderInsuranceTab();
      case 'pharmacy':
        return renderPharmacyTab();
      case 'cases':
        return config.features.multiStage ? <ProfileCasesSection patientId={Number(userId)} /> : null;
      case 'account_settings':
        return renderAccountSettingsTab();
      default:
        return renderProfileTab();
    }
  };

  const renderTabBar = (): React.ReactNode => (
    <div className={collectorStyles.tabsCollector} role="tablist">
      {profileTabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={currentTab === tab.value}
          className={`${collectorStyles.tabCollector} ${currentTab === tab.value ? collectorStyles.tabCollectorActive : ''}`}
          onClick={() => handleTabChange(tab.value)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (isStaffCaseCreationReview) {
    return (
      <>
        <div className={styles.collectorCcPageContent}>
          {showStaffCaseCreationTabs && renderTabBar()}
          {showStaffCaseCreationTabs ? renderTabContent() : renderProfileTab()}
        </div>
        {closeAccountModal && <DeleteAccount setOpenModal={setCloseAccountModal} />}
        {resetPasswordModal && (
          <ResetPasswordModal
            isOpen={resetPasswordModal}
            onClose={() => setResetPasswordModal(false)}
            email={thisUser?.email || ''}
          />
        )}
        {showModal && (
          <UnsavedChangesModal
            isOpen={showModal}
            onConfirm={confirmNavigation}
            saveChange={handleModalSaveChanges}
            loading={false}
            hideModal={hideModal}
          />
        )}
      </>
    );
  }

  if (isAdminReviewProfile) {
    const handleAdminDeactivate = async (targetUserId: number): Promise<void> => {
      try {
        await deleteUserById(targetUserId);
        const updated = { ...thisUser, status: 'Deactivated', deleted_at: new Date().toISOString() } as UserProfile;
        setThisUser(updated);
      } catch (error: unknown) {
        displayErrorDetails(error);
      }
    };

    const renderAdminTabContent = (): React.ReactNode => {
      switch (currentTab) {
        case 'profile':
          return renderProfileTab();
        case 'medical_history':
        case 'dental_history':
          return (
            <CollectorMedicalHistoryTab
              user={thisUser as UserProfile}
              userRole={userInfo.role.name}
              formRef={medicalFormRef}
              setIsDirty={setIsMedicalDirty}
            />
          );
        case 'insurance':
          return (
            <CollectorInsuranceTab
              user={thisUser as UserProfile}
              userRole={userInfo.role.name}
              formRef={insuranceFormRef}
              setIsDirty={setIsInsuranceTabDirty}
              onUserSaved={async () => {
                if (userId) {
                  const res = await getUserById(Number(userId), identityApiRoleForScopedServices);
                  setThisUser(res);
                }
              }}
            />
          );
        case 'pharmacy':
          return (
            <CollectorPharmacyTab
              user={thisUser as UserProfile}
              userRole={userInfo.role.name}
              onUserSaved={async () => {
                if (userId) {
                  const res = await getUserById(Number(userId), identityApiRoleForScopedServices);
                  setThisUser(res);
                }
              }}
            />
          );
        case 'cases':
          return config.features.multiStage ? (
            <ProfileCasesSection patientId={Number(userId)} />
          ) : null;
        case 'account_settings':
          return (
            <CollectorAccountSettingsTab
              user={thisUser as UserProfile}
              userRole={userInfo.role.name}
              onUserSaved={async () => {
                if (userId) {
                  const res = await getUserById(Number(userId), identityApiRoleForScopedServices);
                  setThisUser(res);
                }
              }}
            />
          );
        default:
          return renderProfileTab();
      }
    };

    return (
      <div className={styles.adminReviewShell}>
        <div className={collectorStyles.collectorProfileColumn}>
          {!isAddingNewDependent && (
            <>
              <ModernReviewProfileHeader
                thisUser={thisUser as UserProfile}
                setThisUser={setThisUser as React.Dispatch<React.SetStateAction<UserProfile>>}
                userId={userId}
                resetEmailSent={adminResetEmailSent}
                setResetPasswordConfirmation={setAdminResetPasswordConfirmation}
                setChangeUserStatusFlag={setAdminDeactivateModal}
                setCurrentAvatarURL={setCurrentAvatarURL}
                setAvatarFile={setAvatarFile}
                setAvatarPayload={setAvatarPayload}
                setAvatarAction={setAvatarAction}
                triggerProfileAvatar={handleAdminAvatarTrigger}
                setImage={setImage}
                image={image}
                errors={null}
                roleName={userInfo.role.name}
                onBack={() => navigateRaw(-1)}
                onCreateCase={handleCreateNewCase}
                className={styles.adminReviewHeaderShell}
              />

              {showTabs && renderTabBar()}
            </>
          )}

          {showTabs ? renderAdminTabContent() : renderProfileTab()}
        </div>

        <GenericSnackbar
          open={snackbar.isOpen}
          message={snackbar.message}
          type={snackbar.type}
          onClose={closeSnackbar}
          anchorInDashboardMain
        />

        {closeAccountModal && <DeleteAccount setOpenModal={setCloseAccountModal} />}

        {adminResetPasswordConfirmation.isOpen && thisUser && (
          <AdminResetPasswordModal
            setOpenModal={setAdminResetPasswordConfirmation}
            disableButton={setAdminResetEmailSent}
            userId={thisUser.id}
            userEmail={thisUser.email}
            userName={thisUser.full_name || thisUser.email || 'User'}
            isDeactivated={thisUser.status === 'Deactivated'}
            userState={adminResetPasswordConfirmation.userState}
          />
        )}

        {adminDeactivateModal && thisUser && (
          <ChangeUserStatusModal
            setOpenModal={setAdminDeactivateModal}
            disableButton={() => undefined}
            userId={thisUser.id}
            userName={thisUser.full_name || thisUser.email || 'User'}
            status="deactivated"
            onConfirmDeactivate={handleAdminDeactivate}
          />
        )}

        {showModal && (
          <UnsavedChangesModal
            isOpen={showModal}
            onConfirm={confirmNavigation}
            saveChange={handleModalSaveChanges}
            loading={false}
            hideModal={hideModal}
          />
        )}
      </div>
    );
  }

  const profilePage = (
    <div className={`${styles.profileContainer} ${reviewProfile ? styles.profileContainerReview : ''}`}>
      {!reviewProfile && (
        <>
          <img className={styles.bkgTopImage} src={BlueCircle} alt="profile" />
          <img className={styles.bkgBottomImage} src={GrayCircle} alt="profile" />
          <Sidenav isVisibleOnMobile />
        </>
      )}
      <div className={styles.contentContainer}>
        <div className={styles.parentContainer}>
          {isAdminViewing && (
            <>
              <div className={styles.adminUserHeader}>
                <img
                  className={styles.adminUserAvatar}
                  src={getUserAvatar(thisUser?.id || 0)}
                  alt={thisUser?.full_name || ''}
                  onError={(e: any) => {
                    e.target.src =
                      'https://media.istockphoto.com/id/1130884625/tr/vekt%C3%B6r/kullan%C4%B1c%C4%B1-%C3%BCyesi-vekt%C3%B6r-simgesi-ui-kullan%C4%B1c%C4%B1-arabirimi-veya-profil-face-avatar-uygulamas%C4%B1.jpg?s=612x612&w=0&k=20&c=jAf5nq1ebnZo8TJbjaB9dMHMvgff7uOk67NkF5CpgB0=';
                    e.target.onerror = null;
                  }}
                />
                <div className={styles.adminUserInfo}>
                  <span className={styles.adminUserName}>{thisUser?.full_name}</span>
                  <div className={styles.adminUserStatusRow}>
                    <span className={styles.adminUserStatusLabel}>User status:</span>
                    <span
                      className={`${styles.statusChip} ${
                        thisUser?.status === 'Active'
                          ? styles.statusChipActive
                          : thisUser?.status === 'Deactivated'
                            ? styles.statusChipDeactivated
                            : styles.statusChipOther
                      }`}
                    >
                      {thisUser?.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className={styles.adminBanner}>
                <IoInformationCircle className={styles.adminBannerIcon} />
                <span>You are editing {thisUser?.full_name || 'a user'}&apos;s profile</span>
              </div>
            </>
          )}
          {showTabs && (
            <ProfileCommonHeader
              currentTab={currentTab}
              handleTabChange={handleTabChange}
              tabs={profileTabs}
              className={reviewProfile ? styles.reviewTabs : undefined}
            />
          )}
          {showTabs ? renderTabContent() : renderProfileTab()}
        </div>
      </div>
      {closeAccountModal && <DeleteAccount setOpenModal={setCloseAccountModal} />}
      {resetPasswordModal && (
        <ResetPasswordModal
          isOpen={resetPasswordModal}
          onClose={() => setResetPasswordModal(false)}
          email={thisUser?.email || ''}
        />
      )}
      {showModal && (
        <UnsavedChangesModal
          isOpen={showModal}
          onConfirm={confirmNavigation}
          saveChange={handleModalSaveChanges}
          loading={false}
          hideModal={hideModal}
        />
      )}
    </div>
  );

  /** ReviewProfile already wraps this route in Container + patientDashboard; avoid a second Container (double sidenav). */
  return profilePage;
};
export default MyProfile;
